/**
 * Created by andrey on 16.12.2020.
 */

const EditorMap2d = function (mapAdapter, layer, options) {
    EditorMap2dBase.call(this, mapAdapter, layer, options);

    this.regions = cleverapps.clone(this.regions, true);

    this.commandUid = options.commandUid;

    // the editor shows the whole map, the scroll bounds of the game are kept apart; of several boxes the last one is edited
    this.scrollBounds = this.prepareVisibleBox(cleverapps.clone(options.visibleBox || Map2d.VISIBLE_BOX, true));
    this.visibleBoxes = Array.isArray(options.visibleBox) ? options.visibleBox : undefined;

    this.selection = new EditorMap2dSelection(this);
    this.reference = new EditorMap2dReference(cleverapps.meta.getSelectedLocation().locationConfig.locationBundleId, options.reference);
    this.reference.on("save", this.saveLevel.bind(this));

    this.on("changeBackground", this.saveLevel.bind(this));
};

EditorMap2d.prototype = Object.create(EditorMap2dBase.prototype);
EditorMap2d.prototype.constructor = EditorMap2d;

EditorMap2d.prototype.getDraggable = function (x, y) {
    const fog = this.getFog(x, y);
    const fogId = fog && fog.fogBlock && fog.fogBlock.id;
    const selected = this.fogs.selected;
    const selectedCode = selected && selected.code;

    if (fogId === selectedCode && selectedCode) {
        return [fog.fogBlock.balloon, fog.fogBlock.editorHead].find((draggable) => cc.pointEqualToPoint(draggable && draggable.tile || draggable, cc.p(x, y)));
    }
};

EditorMap2d.prototype.saveLevel = cleverapps.timeredThrottle(cleverapps.parseInterval("1 seconds"), function () {
    cleverapps.administrator.adminLocations.save({
        map: this.getLevelMap(),
        terrains: this.getLevelTerrain(),
        field: this.getLevelField(),
        regions: this.getLevelRegion(),

        decorators: this.decorators.getSaveInfo(),
        reference: this.reference.getSaveInfo(),
        background: this.background,
        ...(this.scrollBoundsChanged && { visibleBox: this.getLevelVisibleBox() }),

        commandUid: this.commandUid
    });
});

EditorMap2d.prototype.getLevelRegion = function () {
    for (const code in this.regions) {
        const region = this.regions[code];
        const positionsUnique = cleverapps.unique(region.positions, Unit.GetPositionKey);
        if (region.positions.length !== positionsUnique.length) {
            region.positions = positionsUnique;
            cleverapps.throwAsync(`Region ${code} positions non unique`);
        }
    }
    return this.regions;
};

EditorMap2d.prototype.getLevelTerrain = function () {
    return this.terrains.map((line, y) => line.map((terrain, x) => (this.layers[Map2d.LAYER_GROUND][y][x] ? terrain : Map2d.TERRAIN_EMPTY)).join(""));
};

EditorMap2d.prototype.getLevelMap = function () {
    const map = [];
    for (let y = 0; y < this.getHeight(); y++) {
        const row = [];
        for (let x = 0; x < this.getWidth(); x++) {
            let value = this.isWaterUnit(x, y) ? Map2d.TILE_WATER_UNIT : Map2d.TILE_WATER;

            if (this.calcGroundLevel(x, y)) {
                value = this.layers[Map2d.LAYER_GROUND][y][x];
            }
            row.push(value);
        }
        map.push(row.join(""));
    }
    return map;
};

EditorMap2d.prototype.getLevelField = function () {
    const field = [];
    for (let y = 0; y < this.getHeight(); y++) {
        for (let x = 0; x < this.getWidth(); x++) {
            this.getUnits(x, y).forEach((unit) => {
                if (!unit.isMultiCellBody()) {
                    const unitData = EditorMap2d.getUnitData(unit);
                    unitData.x = x;
                    unitData.y = y;
                    field.push(unitData);
                }
            });
        }
    }
    return field;
};

EditorMap2d.prototype.add = function (layer, x, y, unit) {
    if (unit && (unit.chooser || unit.head && unit.head.chooser)) {
        return;
    }

    EditorMap2dBase.prototype.add.call(this, layer, x, y, unit);
};

EditorMap2d.prototype.findEmptySlot = function (x, y) {
    const fog = this.getFog(x, y);
    if (fog) {
        return fog.fogBlock.tiles.find((t) => this.isEmpty(t.x, t.y));
    } 
    return Map2d.prototype.findEmptySlot.call(this, x, y);
};

EditorMap2d.prototype.isGround = function (x, y) {
    const groundValue = this.getValue(Map2d.LAYER_GROUND, x, y);
    const waterValue = this.getValue(Map2d.LAYER_WATER, x, y);
    return [Map2d.TILE_GREEN_LEVEL_1, Map2d.TILE_GREEN_LEVEL_2].includes(groundValue) || waterValue === Map2d.TILE_WATER_UNIT;
};

EditorMap2d.prototype.setBackgroundPosition = function (x, y) {
    this.background.x = x / resolutionScale;
    this.background.y = y / resolutionScale;

    this.trigger("changeBackground");
};

EditorMap2d.prototype.setBackgroundScale = function (x, y) {
    this.background.scaleX = x;
    this.background.scaleY = y;

    this.trigger("changeBackground");
};

// the boxes before the last one and its "available" stay as they are
EditorMap2d.prototype.getLevelVisibleBox = function () {
    if (!this.visibleBoxes) {
        return this.scrollBounds;
    }

    const last = this.visibleBoxes[this.visibleBoxes.length - 1];
    return this.visibleBoxes.slice(0, -1).concat({ available: last.available, ...this.scrollBounds });
};

EditorMap2d.prototype.setScrollBounds = function (bounds) {
    this.scrollBounds = bounds;
    this.scrollBoundsChanged = true;

    this.trigger("changeScrollBounds");
    this.saveLevel();
};

Unit.prototype.baseMove = Unit.prototype.move;

Unit.prototype.move = function (x, y, isNewPlace) {
    if (!Editor || !Editor.currentEditor) {
        return this.baseMove(x, y, isNewPlace);
    }

    if (!this.canMoveTo(x, y)) {
        return false;
    }

    const map = Map2d.currentMap;
    const occupied = [];

    this.getShape().forEach((part) => {
        let unit = map.getUnit(x + part.x, y + part.y);
        if (unit) {
            if (unit.head) {
                unit = unit.head;
            }
            if (unit !== this && occupied.indexOf(unit) === -1) {
                occupied.push(unit);
            }
        }
    });

    const occupiedInfo = occupied.map((unit) => ({
        x: unit.x, y: unit.y, hash: unit.getUnitHash(), data: EditorMap2d.getUnitData(unit)
    }));

    Editor.currentEditor.beginBatch();

    occupiedInfo.forEach((data) => {
        Editor.currentEditor.doCommand("deleteUnit", data);
    });

    Editor.currentEditor.doCommand("moveUnit", {
        hash: this.getUnitHash(),
        oldX: this.x,
        oldY: this.y,
        x,
        y
    });

    occupiedInfo.forEach((info) => {
        const freeCell = map.findEmptySlot(x, y, info.data, {
            skipCheckScreen: true,
            skipCheckEqual: true
        });
        if (freeCell) {
            Editor.currentEditor.doCommand("produceUnit", {
                x: freeCell.x, y: freeCell.y, unit: info.data
            });
        }
    });

    Editor.currentEditor.endBatch();
};

Unit.prototype.baseCanMoveTo = Unit.prototype.canMoveTo;

Unit.prototype.canMoveTo = function (x, y) {
    if (!Editor || !Editor.currentEditor) {
        return this.baseCanMoveTo(x, y);
    }

    return this.getShape().every((part) => {
        if (this.isBlockedCell(x + part.x, y + part.y)) {
            return false;
        }

        return true;
    }, this);
};

Unit.prototype.getUnitHash = function () {
    return cleverapps.hashCode(`${this.code}|${this.stage}|${this.x}|${this.y}`);
};

Unit.prototype.onApplyShift = function () {

};

EditorMap2d.getUnitData = function (unit) {
    const data = {
        code: unit.code,
        stage: unit.stage
    };

    if (unit.isLockedUnit()) {
        data.lock = 1;
    }

    const multiple = unit.findComponent(Multiple);
    if (multiple) {
        const fakeUnit = Map2d.currentMap.fogs.getFakeUnits(unit.x, unit.y)[multiple.indexInCell];
        if (fakeUnit) {
            if (fakeUnit.shiftX) {
                data.shiftX = fakeUnit.shiftX;
            }
            if (fakeUnit.shiftY) {
                data.shiftY = fakeUnit.shiftY;
            }
        }
    }

    return data;
};

EditorMap2d.findUnit = function (map, x, y, hash) {
    return map.getUnits(x, y).find((u) => u.getUnitHash() === hash);
};

EditorMap2d.removeMultipleUnit = function (map, unit) {
    const multiple = unit.findComponent(Multiple);
    const removedIndex = multiple.indexInCell;
    const remainingUnits = map.getUnits(unit.x, unit.y).filter((u) => u !== unit);

    remainingUnits.forEach((u, newIndex) => {
        const m = u.findComponent(Multiple);
        m.indexInCell = newIndex;
        m.unitsVisibility.splice(removedIndex, 1);
    });

    const fakeEntry = map.fogs.fakeUnits[unit.x] && map.fogs.fakeUnits[unit.x][unit.y];
    if (Array.isArray(fakeEntry)) {
        fakeEntry.splice(removedIndex, 1);
        if (fakeEntry.length === 0) {
            delete map.fogs.fakeUnits[unit.x][unit.y];
        }
    }

    if (remainingUnits.length === 0) {
        map.remove(Map2d.LAYER_UNITS, unit.x, unit.y);
    } else {
        map.add(Map2d.LAYER_UNITS, unit.x, unit.y, remainingUnits.length === 1 ? remainingUnits[0] : remainingUnits);
    }

    unit.destructor();
};

EditorMap2d.calcRepulsion = function (shifts) {
    const round2 = (v) => Math.round(v * 100) / 100;
    const MIN_DIST = 0.25;
    let repulsed = true;
    for (let iter = 0; repulsed && iter < 20; iter++) {
        repulsed = false;
        for (let i = 0; i < shifts.length; i++) {
            for (let j = i + 1; j < shifts.length; j++) {
                const fi = shifts[i];
                const fj = shifts[j];
                const sx = (fj.shiftX || 0) - (fi.shiftX || 0);
                const sy = (fj.shiftY || 0) - (fi.shiftY || 0);
                const dist = Math.hypot(sx, sy);
                if (dist >= MIN_DIST) {
                    continue;
                }
                repulsed = true;
                const push = (MIN_DIST - dist) / 2;
                const nx = dist > 0 ? sx / dist : 0;
                const ny = dist > 0 ? sy / dist : 1;
                fi.shiftX = cleverapps.clamp(round2((fi.shiftX || 0) - nx * push), -0.5, 0.5);
                fi.shiftY = cleverapps.clamp(round2((fi.shiftY || 0) - ny * push), -0.5, 0.5);
                fj.shiftX = cleverapps.clamp(round2((fj.shiftX || 0) + nx * push), -0.5, 0.5);
                fj.shiftY = cleverapps.clamp(round2((fj.shiftY || 0) + ny * push), -0.5, 0.5);
            }
        }
    }
};

EditorClient.registerCommand("produceUnit", {
    execute: function (editor, command) {
        const map = editor.map;
        const unit = new Unit(command.unit);

        const multiple = unit.findComponent(Multiple);
        if (multiple && map.getUnit(command.x, command.y)) {
            unit.x = command.x;
            unit.y = command.y;

            const existingValue = map.getValue(Map2d.LAYER_UNITS, command.x, command.y);
            if (existingValue instanceof Unit && !existingValue.findComponent(Multiple)) {
                map.setPointer(command.x, command.y);
                map.setValue(Map2d.LAYER_UNITS, Iso.SAME, undefined);
            }
            const existingMultiple = map.getUnits(command.x, command.y).filter((u) => u.findComponent(Multiple));

            multiple.init(existingMultiple.length, existingMultiple.length + 1);
            existingMultiple.forEach((u) => u.findComponent(Multiple).unitsVisibility.push(1));
            map.add(Map2d.LAYER_UNITS, command.x, command.y, multiple.joinWith(map.getValue(Map2d.LAYER_UNITS, command.x, command.y)));
        } else {
            unit.setPosition(command.x, command.y);
            map.setPointer(command.x, command.y);
            map.setValue(Map2d.LAYER_UNITS, Iso.SAME, undefined);
            map.add(Map2d.LAYER_UNITS, command.x, command.y, unit);
        }

        if (multiple) {
            const fakesByX = map.fogs.fakeUnits[command.x] || (map.fogs.fakeUnits[command.x] = {});
            const fakesAtCell = fakesByX[command.y] || (fakesByX[command.y] = []);
            fakesAtCell.push({
                code: command.unit.code,
                stage: command.unit.stage,
                x: command.x,
                y: command.y,
                shiftX: command.unit.shiftX || 0,
                shiftY: command.unit.shiftY || 0
            });
        }

        map.removeTiles([{ x: command.x, y: command.y }]);
        map.showTile(command.x, command.y);
        map.saveLevel();

        const unitView = unit.onGetView();
        if (unitView) {
            unitView.setScale(0);
            unitView.runAction(new cc.ScaleTo(0.2, 1).easing(cc.easeBackOut(1.5)));
            cleverapps.effects.fire("merge.spawn.landing");
        }

        return {
            type: "deleteUnit",
            data: {
                x: command.x,
                y: command.y,
                hash: unit.getUnitHash(),
                ...(multiple && { index: multiple.indexInCell })
            }
        };
    }
});

EditorClient.registerCommand("produceMap", {
    execute: function (editor, command) {
        const map = editor.map;
        const x = command.x;
        const y = command.y;

        const prevState = {
            x,
            y,
            tile: map.getValue(Map2d.LAYER_GROUND, x, y) || map.getValue(Map2d.LAYER_WATER, x, y),
            terrain: map.terrains[y] && map.terrains[y][x]
        };

        map.setPointer(x, y);

        map.setValue(Map2d.LAYER_WATER, Iso.SAME, undefined);
        map.setValue(Map2d.LAYER_GROUND, Iso.SAME, undefined);
        map.setValue(Map2d.ABOVE_GROUND, Iso.SAME, undefined);

        if (command.tile === Map2d.TILE_WATER || command.tile === Map2d.TILE_WATER_UNIT) {
            map.setValue(Map2d.LAYER_UNITS, Iso.SAME, undefined);
            map.setValue(Map2d.LAYER_WATER, Iso.SAME, command.tile);
        } else if (command.tile !== undefined) {
            map.setTerrain(x, y, command.terrain);
            map.setValue(Map2d.LAYER_GROUND, Iso.SAME, command.tile);
            map.setValue(
                Map2d.ABOVE_GROUND,
                Iso.SAME,
                Map2d.IMPASSABLE_LEVELS.includes(command.tile) ? Map2d.TILE_STARRED_GROUND_CHESS : Map2d.TILE_GROUND_CHESS
            );
        }

        let dx, dy;

        for (dy = -2; dy <= 2; dy++) {
            for (dx = -2; dx <= 2; dx++) {
                if (!map.containsCell(x + dx, y + dy)) {
                    continue;
                }

                map.layers.forEach((layer, layerId) => {
                    map.setPointer(x + dx, y + dy);
                    if (!layer[y + dy]) {
                        return;
                    }

                    const value = layer[y + dy][x + dx];
                    map.setValue(layerId, Iso.SAME, value);
                });
            }
        }

        if (Map2d.BORDERS) {
            ViewReader.onEdit(x, y);
        }

        map.edges.onEdit(x, y);

        for (dy = -3; dy <= 3; dy++) {
            for (dx = -3; dx <= 3; dx++) {
                if (map.containsCell(x + dx, y + dy)) {
                    map.removeTiles([{ x: x + dx, y: y + dy }]);
                    map.showTile(x + dx, y + dy);
                }
            }
        }

        map.saveLevel();

        cleverapps.effects.fire(command.tile !== undefined ? "merge.spawn.landing" : "merge.unit.destroy");

        return { type: "produceMap", data: prevState };
    }
});

EditorClient.registerCommand("moveUnit", {
    execute: function (editor, command) {
        const unit = EditorMap2d.findUnit(editor.map, command.oldX, command.oldY, command.hash);
        if (!unit) {
            return;
        }

        command.code = unit.code;
        command.stage = unit.stage;

        editor.map.showTile(command.oldX, command.oldY);

        unit.liftUnitFromMap();
        unit.setPosition(command.x, command.y);
        unit.unhighlight();
        unit.onMove();

        editor.map.saveLevel();

        cleverapps.effects.fire("merge.spawn.landing");

        return {
            type: "moveUnit",
            data: {
                hash: unit.getUnitHash(), oldX: command.x, oldY: command.y, x: command.oldX, y: command.oldY
            }
        };
    }
});

EditorClient.registerCommand("setBackgroundPosition", {
    execute: function (editor, command) {
        const prev = editor.map.getBackgroundPosition();

        editor.map.setBackgroundPosition(command.x, command.y);

        return {
            type: "setBackgroundPosition",
            data: { x: prev.x, y: prev.y }
        };
    }
});

EditorClient.registerCommand("setScrollBounds", {
    execute: function (editor, command) {
        const prev = editor.map.scrollBounds;

        editor.map.setScrollBounds({
            cutLeft: command.cutLeft, cutRight: command.cutRight, cutTop: command.cutTop, cutBottom: command.cutBottom
        });

        return {
            type: "setScrollBounds",
            data: prev
        };
    }
});

EditorClient.registerCommand("setBackgroundScale", {
    execute: function (editor, command) {
        const prev = editor.map.getBackgroundScale();

        editor.map.setBackgroundScale(command.x, command.y);

        return {
            type: "setBackgroundScale",
            data: { x: prev.x, y: prev.y }
        };
    }
});
